import React, { useEffect, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import UndoIcon from "@mui/icons-material/Undo";

import Layout from "../../layout";
import CustomDataTable from "../../components/table/CustomTable";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import DatePickerInput from "../../forms/input/DatePickerInput";
import {
  getPaymentsV2,
  refundPaymentV2,
  PaymentV2,
} from "../../utils/billingServiceV2";
import moment from "moment";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import StatusModal from "../../components/modals/StatusModal";

const PaymentsV2Page: React.FC = () => {
  const [payments, setPayments] = useState<PaymentV2[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [status, setStatus] = useState<string>("");
  const [customerId, setCustomerId] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  // Refund Modal
  const [refundPayment, setRefundPayment] = useState<PaymentV2 | null>(null);
  const [refundReason, setRefundReason] = useState("error by human");
  const [actionLoading, setActionLoading] = useState(false);
  const [statusModal, setStatusModal] = useState<{
    open: boolean;
    type: "success" | "error";
    message: string;
    title: string;
  }>({
    open: false,
    type: "success",
    message: "",
    title: "",
  });

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPaymentsV2({
        page,
        pageSize: rowsPerPage,
        status: status || undefined,
        customerId: customerId || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });

      if (response && response.success) {
        setPayments(response.data);
        setTotalCount(response.totalCount);
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, status, customerId, fromDate, toDate]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefund = async () => {
    if (!refundPayment) return;

    setActionLoading(true);
    try {
      const res = await refundPaymentV2(refundPayment.id, refundReason);
      setRefundPayment(null);
      if (res && res.success) {
        setStatusModal({
          open: true,
          type: "success",
          title: "Refund Successful",
          message: "Payment refunded successfully",
        });
        fetchPayments();
      } else {
        throw res;
      }
    } catch (err: any) {
      console.error("Error refunding payment", err);
      setStatusModal({
        open: true,
        type: "error",
        title: "Refund Failed",
        message: err.message || "Failed to refund payment",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    "Payment Number",
    "Customer",
    "Bill Number",
    "Payment Date",
    "Method",
    "Amount",
    "Status",
    "Actions",
  ];

  const rowsContent = payments.map((payment) => (
    <TableRow key={payment.id}>
      <TableCell>{payment.paymentNumber}</TableCell>
      <TableCell>{payment.customerName}</TableCell>
      <TableCell>{payment.billNumber}</TableCell>
      <TableCell>
        {moment(payment.paymentDate).format("DD MMM YY HH:mm")}
      </TableCell>
      <TableCell>{payment.paymentMethod || "-"}</TableCell>
      <TableCell>₹{payment.amount?.toLocaleString()}</TableCell>
      <TableCell>
        <Chip
          label={payment.status}
          size="small"
          color={
            payment.status === "Completed"
              ? "success"
              : payment.status === "Refunded"
                ? "error"
                : "default"
          }
        />
      </TableCell>
      <TableCell>
        {payment.status === "Completed" && (
          <IconButton
            size="small"
            color="error"
            onClick={() => setRefundPayment(payment)}
            title="Refund Payment"
          >
            <UndoIcon fontSize="small" />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6" fontWeight="bold">
            Payments (v2)
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchPayments}
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            Refresh
          </Button>
        </Stack>

        <Card sx={{ p: 2, mb: 3, borderRadius: "12px" }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CustomOutlinedSelect
              label="Status"
              width="200px"
              options={[
                { label: "All Status", value: "" },
                { label: "Completed", value: "Completed" },
                { label: "Refunded", value: "Refunded" },
              ]}
              value={status}
              onChange={(opt) => {
                setStatus(opt.value as string);
                setPage(0);
              }}
            />
            <DatePickerInput
              label="From Date"
              value={fromDate}
              onChange={(date) => {
                setFromDate(
                  date
                    ? new Date(
                        date.getTime() - date.getTimezoneOffset() * 60000,
                      ).toISOString()
                    : "",
                );
                setPage(0);
              }}
            />
            <DatePickerInput
              label="To Date"
              value={toDate}
              onChange={(date) => {
                setToDate(
                  date
                    ? new Date(
                        date.getTime() - date.getTimezoneOffset() * 60000,
                      ).toISOString()
                    : "",
                );
                setPage(0);
              }}
            />
          </Stack>
        </Card>

        <Card
          sx={{
            borderRadius: "12px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          }}
        >
          <CustomDataTable
            columns={columns}
            rowsContent={rowsContent}
            isLoading={loading}
            pagination
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            Actions
          />
        </Card>
      </Box>

      <ConfirmationModal
        confirmModal={!!refundPayment}
        title="Refund Payment"
        body={`Are you sure you want to refund payment ${refundPayment?.paymentNumber}?`}
        onClose={() => setRefundPayment(null)}
        onClick={handleRefund}
        isLoading={actionLoading}
      />

      <StatusModal
        open={statusModal.open}
        type={statusModal.type}
        title={statusModal.title}
        message={statusModal.message}
        onClose={() => setStatusModal({ ...statusModal, open: false })}
      />
    </Layout>
  );
};

export default PaymentsV2Page;
